// UNIVAPLAT-1906: AdminWhatsappService — wraps 3 WhatsApp admin endpoints.
// Follows codebase pattern: HTTP response is { data: T }; unwrap with .pipe(map(res => res.data)).
import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom, map} from 'rxjs';
import {WhatsAppBulkSendResponse, WhatsAppConversationResponse, WhatsAppFreeTextSendResponse, WhatsAppMessagesResponse, WhatsAppSendResponse, WhatsAppTemplatesResponse} from '@shared/api/models/query/admin-whatsapp.model';
import {AdminWhatsAppSendCommand} from '@shared/api/models/command/admin-whatsapp-send.model';
import {AdminWhatsAppBulkSendCommand, AdminWhatsAppBulkSendPayload} from '@shared/api/models/command/admin-whatsapp-bulk-send.model';
import {environment} from '../../../../../environments/environment';

@Injectable({providedIn: 'root'})
export class AdminWhatsappService {

    constructor(private http: HttpClient) {}

    /** GET /api/v2/integrations/whatsapp/templates */
    async getTemplates(): Promise<WhatsAppTemplatesResponse> {
        return firstValueFrom(
            this.http.get<{data: WhatsAppTemplatesResponse}>(`${environment.baseApiUrl}v2/integrations/whatsapp/templates`)
                .pipe(map(res => res.data))
        );
    }

    /** GET /api/v2/users/:userId/whatsapp/messages */
    async getMessageLog(userId: number): Promise<WhatsAppMessagesResponse> {
        return firstValueFrom(
            this.http.get<{data: WhatsAppMessagesResponse}>(`${environment.baseApiUrl}v2/users/${userId}/whatsapp/messages`)
                .pipe(map(res => res.data))
        );
    }

    /** POST /api/v2/users/:userId/whatsapp/messages */
    async sendMessage(userId: number, payload: AdminWhatsAppSendCommand): Promise<WhatsAppSendResponse> {
        return firstValueFrom(
            this.http.post<{data: WhatsAppSendResponse}>(`${environment.baseApiUrl}v2/users/${userId}/whatsapp/messages`, payload)
                .pipe(map(res => res.data))
        );
    }

    /**
     * UNIVAPLAT-2548: POST /api/v2/integrations/whatsapp/bulk-sends (admin-only).
     * Bulk template send to every user matching the embedded admin-dashboard filter payload.
     * Recipients are re-resolved SERVER-SIDE from the filters — no recipient list is sent.
     *
     * UNIVAPLAT-2534 fix: the command carries the dashboard's array-typed filter state, but the
     * backend `SendBulkWhatsAppMessageRequest` expects comma-separated STRINGS (same convention
     * as the GET /v2/users query string). Serialization happens here — see toBulkSendPayload() —
     * so every caller gets the correct wire format without knowing about it.
     */
    async sendBulkMessage(command: AdminWhatsAppBulkSendCommand): Promise<WhatsAppBulkSendResponse> {
        const res = await firstValueFrom(
            this.http.post<{data: WhatsAppBulkSendResponse}>(
                `${environment.baseApiUrl}v2/integrations/whatsapp/bulk-sends`,
                AdminWhatsappService.toBulkSendPayload(command)
            )
        );
        // Same SampleInterceptor caveat as getConversation: a swallowed 4xx/5xx resolves with a
        // string instead of the {data} envelope. Validate the shape so the confirm modal gets a
        // rejection (and shows its error path) instead of rendering "undefined recipients".
        if (typeof res?.data?.bulkSendId !== 'number' || typeof res?.data?.recipientCount !== 'number') {
            throw new Error('Bulk send did not return a valid response.');
        }
        return res.data;
    }

    /**
     * UNIVAPLAT-2534: serialize the array-typed filter state into the backend wire format —
     * a deliberate mirror of AdminUsersService.buildFilterParams() so the audience the admin
     * previewed on the dashboard resolves IDENTICALLY at send time:
     *
     * - every multi-value filter is comma-joined (`['a','b']` → `'a,b'`), omitted when empty;
     * - `role` defaults to 'patient,carer' when the admin picked no role filter — the dashboard
     *   GET applies the same default, so the previewed list never contains other roles. Omitting
     *   it here would silently EXPAND the audience beyond what the admin saw;
     * - `includeDemo` is always sent as 0/1 (backend types it ?int; 0 meaningfully excludes
     *   demo users, matching the GET path's `includeDemo=0`);
     * - date-range fields pass through as strings when set.
     */
    private static toBulkSendPayload(command: AdminWhatsAppBulkSendCommand): AdminWhatsAppBulkSendPayload {
        const payload: AdminWhatsAppBulkSendPayload = {
            templateSid: command.templateSid,
            variables: command.variables,
            role: command.role?.length ? command.role.join(',') : 'patient,carer',
            includeDemo: command.includeDemo ? 1 : 0,
        };

        if (command.license?.length) { payload.license = command.license.join(','); }
        if (command.careProgramme?.length) { payload.careProgramme = command.careProgramme.join(','); }
        if (command.category?.length) { payload.category = command.category.join(','); }
        if (command.organisation?.length) { payload.organisation = command.organisation.join(','); }
        if (command.service?.length) { payload.service = command.service.join(','); }
        if (command.department?.length) { payload.department = command.department.join(','); }
        if (command.status?.length) { payload.status = command.status.join(','); }
        if (command.currentWeek?.length) { payload.currentWeek = command.currentWeek.join(','); }
        if (command.engagement?.length) { payload.engagement = command.engagement.join(','); }

        if (command.signupDateStart) { payload.signupDateStart = command.signupDateStart; }
        if (command.signupDateEnd) { payload.signupDateEnd = command.signupDateEnd; }
        if (command.firstLoginDateStart) { payload.firstLoginDateStart = command.firstLoginDateStart; }
        if (command.firstLoginDateEnd) { payload.firstLoginDateEnd = command.firstLoginDateEnd; }
        if (command.lastLoginDateStart) { payload.lastLoginDateStart = command.lastLoginDateStart; }
        if (command.lastLoginDateEnd) { payload.lastLoginDateEnd = command.lastLoginDateEnd; }

        return payload;
    }

    /**
     * UNIVAPLAT-1904: POST /api/v2/integrations/whatsapp/users/:userId/messages
     * Free-text send within the 24h Twilio session window (conversation panel send box).
     * Returns 422 with error code `whatsapp_outside_session_window` when the window has closed.
     */
    async sendFreeTextMessage(userId: number, message: string): Promise<WhatsAppFreeTextSendResponse> {
        return firstValueFrom(
            this.http.post<{data: WhatsAppFreeTextSendResponse}>(
                `${environment.baseApiUrl}v2/integrations/whatsapp/users/${userId}/messages`,
                {message}
            ).pipe(map(res => res.data))
        );
    }

    /**
     * WhatsApp media display design (2026-07-30, §6): GET a media attachment as a Blob through
     * the normal auth interceptor. `path` is the relative API path served in
     * WhatsAppConversationItem.media[].url — an opaque value passed verbatim, e.g.
     * "/api/v2/integrations/whatsapp/inbound-messages/45/media/0".
     * Raw bytes — no {data} envelope to unwrap.
     */
    async getMediaBlob(path: string): Promise<Blob> {
        // `path` comes from an API payload. Only relative /api/ paths are legitimate (design §3);
        // an absolute or protocol-relative value ("https://…", "//evil…") would make `new URL`
        // resolve to a foreign host, and the auth interceptor attaches the admin's bearer token
        // to every request regardless of host. Reject before building any request.
        if (!path.startsWith('/api/')) {
            throw new Error('Refused media path: not a relative /api/ path.');
        }
        // baseApiUrl ends in "/api/" and `path` starts with "/api/", so resolve against the
        // origin rather than concatenating (avoids ".../api/api/v2/...").
        const url = new URL(path, environment.baseApiUrl).toString();
        const blob = await firstValueFrom(this.http.get(url, {responseType: 'blob'}));
        // The app-wide SampleInterceptor swallows 401/403/404/500 and RESOLVES with a string
        // (err.message) instead of rejecting. Reject explicitly on a non-Blob value so callers
        // see a failure directly, not via an accidental TypeError in URL.createObjectURL.
        if (!(blob instanceof Blob)) {
            throw new Error('Media request did not return binary content.');
        }
        return blob;
    }

    /**
     * UNIVAPLAT-1904: GET /api/v2/integrations/whatsapp/users/:userId/conversation
     * Unified inbound + outbound conversation, cursor-paginated (newest page when `before` omitted).
     */
    async getConversation(userId: number, before?: string): Promise<WhatsAppConversationResponse> {
        const url = `${environment.baseApiUrl}v2/integrations/whatsapp/users/${userId}/conversation`;
        const res = await firstValueFrom(
            this.http.get<{data: WhatsAppConversationResponse}>(url, {
                params: before ? {before} : {}
            })
        );
        // Same SampleInterceptor caveat as getMediaBlob: a swallowed 4xx/5xx resolves with a
        // string, not the {data} envelope. Validate the shape so callers get a rejection
        // instead of a downstream crash on `response.items`.
        if (!Array.isArray(res?.data?.items)) {
            throw new Error('Conversation request did not return an item list.');
        }
        return res.data;
    }
}
